import { Box, Wrap, WrapItem, Button, Text, VStack } from '@chakra-ui/react';
import { UtensilsCrossed, Wine, Coffee, Droplet } from 'lucide-react';
import { USER_SELECTABLE_COURSES, type CourseType } from '../hooks/useRestaurantRating';
import { haptics } from '@/utils/haptics';

interface CourseTagSelectorProps {
  /** Currently selected course */
  value: CourseType | null;
  /** Callback when a course is selected */
  onChange: (course: CourseType) => void;
  /** Show as compact inline selector */
  compact?: boolean;
}

// Group courses by category for better organization
const COURSE_GROUPS = {
  food: {
    label: 'Food',
    icon: UtensilsCrossed,
    courses: ['appetizer', 'main_course', 'dessert'],
  },
  beverages: {
    label: 'Beverages',
    icon: Coffee,
    courses: ['juice', 'soft_drinks', 'hot_drinks', 'water'],
  },
  alcohol: {
    label: 'Alcohol',
    icon: Wine,
    courses: ['wine', 'beer', 'spirits', 'cocktails', 'bottled_cocktails'],
  },
  'zero-proof': {
    label: 'Zero Proof',
    icon: Droplet,
    courses: ['zero_proof'],
  },
} as const;

/**
 * Course type selector for menu items
 * Displays available course types (excluding salad/soup) as selectable tags
 */
export function CourseTagSelector({
  value,
  onChange,
  compact = false,
}: CourseTagSelectorProps) {
  const handleSelect = (course: CourseType) => {
    haptics.selection();
    onChange(course);
  };

  // Get course label from the constant
  const getCourseLabel = (courseValue: string): string => {
    return (
      USER_SELECTABLE_COURSES.find((c) => c.value === courseValue)?.label ?? courseValue
    );
  };

  if (compact) {
    // Compact mode: single row of all options
    return (
      <Wrap spacing={2}>
        {USER_SELECTABLE_COURSES.map((course) => {
          const isSelected = value === course.value;
          return (
            <WrapItem key={course.value}>
              <Button
                size="xs"
                variant={isSelected ? 'solid' : 'outline'}
                colorScheme={isSelected ? 'purple' : 'gray'}
                color={'white'}
                onClick={() => handleSelect(course.value)}
                borderRadius="full"
                fontWeight={isSelected ? 'bold' : 'normal'}
              >
                {course.label}
              </Button>
            </WrapItem>
          );
        })}
      </Wrap>
    );
  }

  // Full mode: grouped by category
  return (
    <VStack spacing={4} align="stretch" w="100%">
      {Object.entries(COURSE_GROUPS).map(([groupKey, group]) => {
        const Icon = group.icon;
        const groupCourses = group.courses.filter((c) =>
          USER_SELECTABLE_COURSES.some((uc) => uc.value === c)
        );

        if (groupCourses.length === 0) return null;

        return (
          <Box key={groupKey}>
            {/* Group header */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <Icon size={14} color="var(--chakra-colors-gray-500)" />
              <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase">
                {group.label}
              </Text>
            </Box>

            {/* Course tags */}
            <Wrap spacing={2}>
              {groupCourses.map((courseValue) => {
                const isSelected = value === courseValue;
                return (
                  <WrapItem key={courseValue}>
                    <Button
                      size="sm"
                      variant={isSelected ? 'solid' : 'outline'}
                      colorScheme={isSelected ? 'purple' : 'gray'}
                      onClick={() => handleSelect(courseValue as CourseType)}
                      borderRadius="full"
                      fontWeight={isSelected ? 'bold' : 'normal'}
                      _hover={{
                        bg: isSelected ? 'purple.600' : 'gray.100',
                      }}
                    >
                      {getCourseLabel(courseValue)}
                    </Button>
                  </WrapItem>
                );
              })}
            </Wrap>
          </Box>
        );
      })}
    </VStack>
  );
}

export default CourseTagSelector;
